import React from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';

interface FooterLinksProps {
  lang?: 'en' | 'fr' | 'de' | 'it';
  animated?: boolean;
  className?: string;
}

const FooterLinks: React.FC<FooterLinksProps> = ({ 
  lang: propLang,
  animated = true,
  className = ''
}) => {
  const { lang: paramLang } = useParams<{ lang?: string }>();
  const currentLang = (propLang || paramLang || 'en') as 'en' | 'fr' | 'de' | 'it';

  const translations = {
    en: {
      madeWith: 'Made with',
      by: 'by',
      privacy: 'Privacy Policy',
      terms: 'Terms of Use',
      contact: 'Contact',
      copyright: `© ${new Date().getFullYear()} TTStats.ch. All rights reserved.`
    },
    fr: {
      madeWith: 'Fait avec',
      by: 'par',
      privacy: 'Politique de confidentialité',
      terms: "Conditions d'utilisation",
      contact: 'Contact',
      copyright: `© ${new Date().getFullYear()} TTStats.ch. Tous droits réservés.`
    },
    de: {
      madeWith: 'Gemacht mit',
      by: 'von',
      privacy: 'Datenschutzerklärung',
      terms: 'Nutzungsbedingungen',
      contact: 'Kontakt',
      copyright: `© ${new Date().getFullYear()} TTStats.ch. Alle Rechte vorbehalten.`
    },
    it: {
      madeWith: 'Fatto con',
      by: 'da',
      privacy: 'Informativa sulla privacy',
      terms: "Termini d'uso",
      contact: 'Contatto',
      copyright: `© ${new Date().getFullYear()} TTStats.ch. Tutti i diritti riservati.`
    }
  };

  const t = translations[currentLang] || translations.en;

  const content = (
    <div className={`text-center space-y-3 ${className}`}>
      {/* Legal Links */}
      <div className="flex items-center justify-center gap-4 text-xs">
        <a
          href="/privacy"
          className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
          {t.privacy}
        </a>
        <span className="text-gray-300">•</span>
        <a
          href="/terms"
          className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
          {t.terms}
        </a>
        <span className="text-gray-300">•</span>
        <a
          href="mailto:doruk@ttstats.ch"
          className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
          {t.contact}
        </a>
      </div>

      {/* Made with Love - More stylish */}
      <div className="flex items-center justify-center gap-2 text-xs">
        <span className="text-gray-400">{t.madeWith}</span>
        <motion.span 
          className="text-red-500"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
        >
          ❤️
        </motion.span>
        <span className="text-gray-400">{t.by}</span>
        <a 
          href="https://doruk.ch" 
          target="_blank" 
          rel="noopener noreferrer"
          className="font-medium text-gray-500 hover:text-gray-700 transition-all duration-200 hover:underline decoration-gray-400 underline-offset-2"
        >
          Doruk Tan Ozturk
        </a>
      </div>

      {/* Copyright */}
      <div className="text-xs text-gray-400">
        {t.copyright}
      </div>
    </div>
  );

  if (!animated) {
    return content;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
    >
      {content}
    </motion.div>
  );
};

export default FooterLinks;